<template>
    <div class="multi-faqs">
        <h3>{{ blok.title }}</h3>
        <L4Accordion
            v-for="(item, index) in accordionsData"
            :key="item._uid"
            :is-open="item.isOpen as boolean"
            :data-question-index="index"
            @toggle="handleToggle(item._uid)"
        >
            <template #title
                ><h2>{{ item.title }}</h2></template
            >
            <template #body>
                <RichText :richtext="item.richTextContent"></RichText>
            </template>
        </L4Accordion>
        <a v-if="blok.items.length > (blok.show_items || 3)" class="more !font-semibold" @click="showFullTrigger()">
            {{ isShowFull ? 'Less' : 'More' }}
            <NuxtIcon v-if="isShowFull" name="ri:arrow-up-s-line" />
            <NuxtIcon v-else name="ri:arrow-down-s-line" />
        </a>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
import L4Accordion from '~/storyblok/L4Accordion.vue';
import type { IAccordionState, IRichText, IItem, IMultiFaqsSection } from '~/types/components/L4HelpTabs';

const { blok } = defineProps<{ blok: IMultiFaqsSection }>();

const accordionsState: IAccordionState[] = blok.items.map((item) => ({
    _uid: item._uid,
    isOpen: false,
    richTextContent: item.content,
}));

const accordions: Ref<IAccordionState[]> = ref(accordionsState);
const isShowFull = ref(false);

const showFullTrigger = () => (isShowFull.value = !isShowFull.value);

const accordionsData = computed(() => {
    const items = isShowFull.value ? blok.items : blok.items.slice(0, blok.show_items || 3);
    return items.map((question) => ({
        ...question,
        ...accordions.value.find((accordion) => question._uid === accordion._uid),
    }));
});

const handleToggle = (toggleId: string) => {
    accordions.value = accordions.value.map((accordion) => {
        if (accordion._uid === toggleId) {
            return {
                ...accordion,
                isOpen: !accordion.isOpen,
            };
        } else {
            return {
                ...accordion,
                isOpen: false,
            };
        }
    });
};

onMounted(() => {
    setTimeout(() => {
        const questionIndex = route.query.question ? parseInt(route.query.question as string) - 1 : -1;
        if (questionIndex !== -1 && accordions.value[questionIndex]) {
            accordions.value[questionIndex].isOpen = true;

            const accordionElement = document.querySelector(`.accordion[data-question-index="${questionIndex}"]`);

            if (accordionElement) {
                const windowWidth = window.innerWidth;

                const headerOffset = windowWidth <= 900 ? 64 : 136;

                const elementPosition = accordionElement.getBoundingClientRect().top;

                const offsetPosition = elementPosition + window.scrollY - headerOffset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                });
            }
        }
    });
});
</script>

<style scoped lang="postcss">
h3 {
    @apply text-base font-semibold mt-[2.2rem] mb-4;
}

.multi-faqs :deep(.accordion-title),
.more {
    @apply block font-normal text-primary pb-4 cursor-pointer;

    &:hover {
        @apply underline;
    }
}
</style>
